export type SurgicalProcedureType = "OUTPATIENT" | "INPATIENT";

export const DEFAULT_STATE_MULTIPLIER = 1;

// Largest value the backend column can hold:
// SurgicalPricingCalculation.state_multiplier is DecimalField(max_digits=4, decimal_places=2).
export const MAX_STATE_MULTIPLIER = 99.99;

type StateMultiplierOverride = Partial<Record<SurgicalProcedureType, number>>;

// States whose surgical pricing default multiplier is not 1.0.
//
// Source of truth: the "State multipliers.xlsx" spreadsheet attached to Linear
// issue KAB-3525 (https://linear.app/kaboodle/issue/KAB-3525). "INPATIENT"
// corresponds to its "inpt" column and "OUTPATIENT" to its "asc" (ambulatory
// surgical center) column; the spreadsheet's "KA" row is Kansas (KS). Any state
// or procedure type not listed here defaults to DEFAULT_STATE_MULTIPLIER. When
// the spreadsheet changes, update this table and the matching snapshot in
// __tests__/stateMultipliers.test.ts.
export const STATE_MULTIPLIER_OVERRIDES: Record<
  string,
  StateMultiplierOverride
> = {
  CA: { INPATIENT: 1.2 },
  CT: { INPATIENT: 1.74 },
  DC: { INPATIENT: 1.13 },
  IN: { INPATIENT: 2 },
  NC: { INPATIENT: 1.8 },
  OH: { INPATIENT: 1.75 },
  PA: { INPATIENT: 1.13 },
  SC: { OUTPATIENT: 1.4 },
  WV: { OUTPATIENT: 1.35 },
  TN: { OUTPATIENT: 1.5 },
  HI: { INPATIENT: 1.9 },
  MS: { INPATIENT: 2 },
  CO: { INPATIENT: 1.18 },
  KS: { INPATIENT: 1.18 },
  MN: { INPATIENT: 1.18 },
};

export const getDefaultStateMultiplier = (
  state: string | null | undefined,
  procedureType: SurgicalProcedureType,
): number => {
  if (!state) return DEFAULT_STATE_MULTIPLIER;
  const override = STATE_MULTIPLIER_OVERRIDES[state.trim().toUpperCase()];
  return override?.[procedureType] ?? DEFAULT_STATE_MULTIPLIER;
};
